import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon';
import { detectFollowups, groupBuckets, type FollowUp, type Buckets } from '../utils/noteAnalysis';
import {
  useArchiveNote,
  useAutoTitleNote,
  useCreateNote,
  useDeleteNote,
  useDuplicateNote,
  useNotes,
  usePurgeNote,
  useRestoreNote,
  useTogglePinNote,
  useUpdateNote,
} from '../hooks/useNotes';
import { flushNoteUpdate, sendMessage } from '../api/client';
import type { NoteResponse, UpdateNoteRequest } from '../api/types';

const AUTOSAVE_DEBOUNCE_MS = 1500;
const TOAST_DURATION_MS = 4000;

type NoteView = 'active' | 'archive' | 'trash';

interface ToastState {
  message: string;
  undo?: () => void;
}

export function NotesView() {
  const [view, setView] = useState<NoteView>('active');
  const { data, isLoading, error } = useNotes(view);
  const activeQuery = useNotes('active');
  const archiveQuery = useNotes('archive');
  const trashQuery = useNotes('trash');

  const create = useCreateNote();
  const update = useUpdateNote();
  const togglePin = useTogglePinNote();
  const remove = useDeleteNote();
  const archive = useArchiveNote();
  const restore = useRestoreNote();
  const purge = usePurgeNote();
  const duplicate = useDuplicateNote();

  const allNotes = data?.notes ?? [];
  const [search, setSearch] = useState('');
  const notes = search.trim()
    ? allNotes.filter((n) => {
        const q = search.toLowerCase();
        return (
          (n.title ?? '').toLowerCase().includes(q) ||
          (n.bodyMarkdown ?? '').toLowerCase().includes(q) ||
          (n.summary ?? '').toLowerCase().includes(q)
        );
      })
    : allNotes;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);

  const showToast = useCallback((msg: string, undo?: () => void) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast({ message: msg, undo });
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }, []);

  const dismissToast = useCallback(() => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  const selected = notes.find((n) => n.id === selectedId) ?? null;
  useEffect(() => {
    if (!selectedId && notes.length > 0) {
      setSelectedId(notes[0].id);
    }
    if (selectedId && !notes.some((n) => n.id === selectedId)) {
      setSelectedId(notes[0]?.id ?? null);
    }
  }, [notes, selectedId]);

  const [createError, setCreateError] = useState<string | null>(null);
  const handleNew = async () => {
    setCreateError(null);
    try {
      const note = await create.mutateAsync({ bodyMarkdown: '# \n\n' });
      setSelectedId(note.id);
      setView('active');
    } catch {
      setCreateError("Couldn't create a note just now — try again.");
    }
  };

  const handleArchive = (id: string, title: string) => {
    archive.mutate(id);
    showToast(`"${title}" archived`, () => restore.mutate(id));
  };

  const handleRestore = (id: string, title: string) => {
    restore.mutate(id);
    showToast(`"${title}" restored`);
  };

  const handleDelete = (id: string, title: string) => {
    remove.mutate(id);
    showToast(`"${title}" moved to trash`, () => restore.mutate(id));
  };

  const handlePurge = (id: string, title: string) => {
    if (window.confirm(`Permanently delete "${title}"? This cannot be undone.`)) {
      purge.mutate(id);
      showToast(`"${title}" permanently deleted`);
    }
  };

  const handleDuplicate = (id: string, title: string) => {
    duplicate.mutate(id);
    showToast(`"${title}" duplicated`);
  };

  const activeCount = activeQuery.data?.notes.length ?? 0;
  const archiveCount = archiveQuery.data?.notes.length ?? 0;
  const trashCount = trashQuery.data?.notes.length ?? 0;

  return (
    <section
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        background: 'var(--rc-bg)',
        position: 'relative',
      }}
    >
      <aside
        className="rc-scroll"
        style={{
          width: 360,
          borderRight: '1px solid var(--rc-border)',
          padding: '20px 16px 28px',
          background: 'var(--bg-elev)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <h2
            style={{
              font: '500 22px/1.15 var(--rc-display)',
              letterSpacing: '-0.015em',
              margin: 0,
              color: 'var(--rc-ink)',
            }}
          >
            Notes
          </h2>
          <button
            type="button"
            onClick={handleNew}
            disabled={create.isPending}
            aria-label="New note"
            style={newBtnStyle}
          >
            <Icon name="plus" size={14} stroke={2.2} />
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Icon
            name="search"
            size={15}
            stroke={1.7}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--ink-3)',
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            placeholder="Search notes, or just ask Jotly..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px 8px 32px',
              background: 'var(--bg-sunken)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              font: '400 13.5px/1.4 var(--font-body)',
              color: 'var(--ink)',
              outline: 'none',
            }}
          />
        </div>

        <ViewTabs
          view={view}
          onChangeView={setView}
          activeCount={activeCount}
          archiveCount={archiveCount}
          trashCount={trashCount}
        />

        {createError && (
          <div
            role="alert"
            style={{
              font: '400 14px/1.45 var(--rc-sans)',
              color: 'var(--rc-warn)',
              marginBottom: 10,
            }}
          >
            {createError}
          </div>
        )}

        {isLoading ? (
          <ListSkeleton />
        ) : error ? (
          <ListError error={error} />
        ) : notes.length === 0 ? (
          <EmptyState view={view} onNew={handleNew} pending={create.isPending} />
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
            {notes.map((n) => (
              <NoteRow
                key={n.id}
                note={n}
                view={view}
                active={n.id === selectedId}
                onSelect={() => setSelectedId(n.id)}
                onPin={() => togglePin.mutate(n.id)}
                onDuplicate={() => handleDuplicate(n.id, n.title || 'Untitled note')}
                onArchive={() => handleArchive(n.id, n.title || 'Untitled note')}
                onDelete={() => handleDelete(n.id, n.title || 'Untitled note')}
                onRestore={() => handleRestore(n.id, n.title || 'Untitled note')}
                onPurge={() => handlePurge(n.id, n.title || 'Untitled note')}
              />
            ))}
          </ul>
        )}
      </aside>

      <main style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
        {selected ? (
          <NoteEditor
            key={selected.id}
            note={selected}
            view={view}
            onSave={(req) => update.mutate({ id: selected.id, req })}
            onTogglePin={() => togglePin.mutate(selected.id)}
            onDelete={() => handleDelete(selected.id, selected.title || 'Untitled note')}
            onArchive={() => handleArchive(selected.id, selected.title || 'Untitled note')}
          />
        ) : (
          <EditorBlank onNew={handleNew} pending={create.isPending} />
        )}
      </main>

      {toast && (
        <UndoToast
          message={toast.message}
          onUndo={toast.undo ? () => { toast.undo!(); dismissToast(); } : undefined}
          onDismiss={dismissToast}
        />
      )}
    </section>
  );
}

// --- View tabs ---

function ViewTabs({
  view,
  onChangeView,
  activeCount,
  archiveCount,
  trashCount,
}: {
  view: NoteView;
  onChangeView: (v: NoteView) => void;
  activeCount: number;
  archiveCount: number;
  trashCount: number;
}) {
  return (
    <div style={viewTabsBarStyle}>
      <button
        type="button"
        onClick={() => onChangeView('active')}
        style={viewTabStyle(view === 'active')}
      >
        <Icon name="list" size={13} stroke={2} />
        Active
        <span style={tabCountStyle(view === 'active')}>{activeCount}</span>
      </button>
      <button
        type="button"
        onClick={() => onChangeView('archive')}
        style={viewTabStyle(view === 'archive')}
      >
        <Icon name="archive" size={13} stroke={2} />
        Archive
        <span style={tabCountStyle(view === 'archive')}>{archiveCount}</span>
      </button>
      <button
        type="button"
        onClick={() => onChangeView('trash')}
        style={viewTabStyle(view === 'trash')}
      >
        <Icon name="trash" size={13} stroke={2} />
        Trash
        <span style={tabCountStyle(view === 'trash')}>{trashCount}</span>
      </button>
    </div>
  );
}

// --- Sidebar list row ---

function NoteRow({
  note,
  view,
  active,
  onSelect,
  onPin,
  onDuplicate,
  onArchive,
  onDelete,
  onRestore,
  onPurge,
}: {
  note: NoteResponse;
  view: NoteView;
  active: boolean;
  onSelect: () => void;
  onPin: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onPurge: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <li
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ position: 'relative' }}
    >
      <button
        type="button"
        onClick={onSelect}
        style={{
          width: '100%',
          textAlign: 'left',
          appearance: 'none',
          border: 0,
          cursor: 'pointer',
          background: active ? 'var(--bg-sunken)' : 'transparent',
          padding: '10px 12px',
          borderRadius: 10,
          display: 'grid',
          gap: 4,
        }}
      >
        <span
          style={{
            font: '600 13px/1.25 var(--rc-sans)',
            color: 'var(--rc-ink)',
            letterSpacing: '-0.005em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {note.title || 'Untitled note'}
        </span>
        {note.summary && (
          <span
            style={{
              font: '400 12px/1.4 var(--rc-sans)',
              color: 'var(--rc-ink-3)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {note.summary}
          </span>
        )}
        <KickChip track={note.track} />
        {(() => {
          const counts = taskStateCounts(note.bodyMarkdown);
          const hasAny = counts.todo + counts.doing + counts.urgent + counts.done > 0;
          if (!hasAny) return null;
          return (
            <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
              {counts.urgent > 0 && (
                <span style={listPillStyle('urgent')}>{counts.urgent} urgent</span>
              )}
              {counts.doing > 0 && (
                <span style={listPillStyle('doing')}>{counts.doing} doing</span>
              )}
              {counts.todo > 0 && (
                <span style={listPillStyle('todo')}>{counts.todo} to do</span>
              )}
              {counts.done > 0 && (
                <span style={listPillStyle('done')}>{counts.done} done</span>
              )}
            </span>
          );
        })()}
      </button>

      {hovered && (
        <div style={hoverActionsStyle}>
          {view === 'active' && (
            <>
              <HoverBtn
                icon="pin"
                label={note.pinnedAt ? 'Unpin' : 'Pin'}
                active={!!note.pinnedAt}
                onClick={(e) => { e.stopPropagation(); onPin(); }}
              />
              <HoverBtn icon="copy" label="Duplicate" onClick={(e) => { e.stopPropagation(); onDuplicate(); }} />
              <HoverBtn icon="archive" label="Archive" onClick={(e) => { e.stopPropagation(); onArchive(); }} />
              <HoverBtn icon="trash" label="Delete" onClick={(e) => { e.stopPropagation(); onDelete(); }} />
            </>
          )}
          {view === 'archive' && (
            <HoverBtn icon="rotate-ccw" label="Restore" onClick={(e) => { e.stopPropagation(); onRestore(); }} />
          )}
          {view === 'trash' && (
            <>
              <HoverBtn icon="rotate-ccw" label="Restore" onClick={(e) => { e.stopPropagation(); onRestore(); }} />
              <HoverBtn icon="trash" label="Delete forever" onClick={(e) => { e.stopPropagation(); onPurge(); }} />
            </>
          )}
        </div>
      )}
    </li>
  );
}

function HoverBtn({
  icon,
  label,
  active,
  onClick,
}: {
  icon: 'pin' | 'copy' | 'archive' | 'trash' | 'rotate-ccw';
  label: string;
  active?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={hoverBtnStyle(active)}
    >
      <Icon name={icon} size={13} stroke={2} />
    </button>
  );
}

function KickChip({ track }: { track: string | null }) {
  const label = track === 'Home' ? 'Home' : 'Work';
  const dotColour = track === 'Home' ? '#5f9e6a' : 'var(--accent)';
  return (
    <span style={kickChipStyle}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: dotColour, flexShrink: 0 }} />
      {label}
    </span>
  );
}

// --- Editor pane ---

function NoteEditor({
  note,
  view,
  onSave,
  onTogglePin,
  onDelete,
  onArchive,
}: {
  note: NoteResponse;
  view: NoteView;
  onSave: (req: UpdateNoteRequest) => void;
  onTogglePin: () => void;
  onDelete: () => void;
  onArchive: () => void;
}) {
  const [body, setBody] = useState(note.bodyMarkdown);
  const [savedSnapshot, setSavedSnapshot] = useState(note.bodyMarkdown);
  const [showBuckets, setShowBuckets] = useState(false);
  const [followUpExpanded, setFollowUpExpanded] = useState(false);
  const [voiceFoldoutOpen, setVoiceFoldoutOpen] = useState(false);
  const [localTrack, setLocalTrack] = useState<string>(note.track === 'Home' ? 'Home' : 'Work');
  const timerRef = useRef<number | undefined>(undefined);

  const followUps = useMemo(() => detectFollowups(body, false), [body]);
  const buckets = useMemo(() => groupBuckets(body), [body]);

  useEffect(() => {
    if (body !== savedSnapshot) return;
    if (note.bodyMarkdown === savedSnapshot) return;
    setBody(note.bodyMarkdown);
    setSavedSnapshot(note.bodyMarkdown);
  }, [note.bodyMarkdown, body, savedSnapshot]);

  const commitBody = (next: string) => {
    setBody(next);
    onSave({ bodyMarkdown: next, track: localTrack });
    setSavedSnapshot(next);
  };

  useEffect(() => {
    if (body === savedSnapshot) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      onSave({ bodyMarkdown: body, track: localTrack });
      setSavedSnapshot(body);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [body, savedSnapshot, onSave, localTrack]);

  useEffect(() => {
    const handler = () => {
      if (body !== savedSnapshot) {
        flushNoteUpdate(note.id, { bodyMarkdown: body, track: localTrack });
        setSavedSnapshot(body);
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [body, savedSnapshot, note.id, localTrack]);

  const toggleTrack = () => {
    const next = localTrack === 'Work' ? 'Home' : 'Work';
    setLocalTrack(next);
    onSave({ bodyMarkdown: body, track: next });
  };

  const isSaved = body === savedSnapshot;

  // Trash countdown: days remaining out of 30 from purgedAt
  const trashCountdown = (() => {
    if (view !== 'trash' || !note.purgedAt) return null;
    const purgedMs = new Date(note.purgedAt).getTime();
    const nowMs = Date.now();
    const elapsedDays = Math.floor((nowMs - purgedMs) / (1000 * 60 * 60 * 24));
    const remaining = Math.max(0, 30 - elapsedDays);
    return remaining;
  })();

  // Voice transcript: extract from body if present (fenced block marked "From voice")
  const voiceTranscript = (() => {
    const marker = '<!-- voice-transcript';
    const idx = body.indexOf(marker);
    if (idx === -1) return null;
    const endMarker = '-->';
    const endIdx = body.indexOf(endMarker, idx + marker.length);
    if (endIdx === -1) return null;
    return body.slice(idx + marker.length, endIdx).trim();
  })();

  return (
    <div
      className="rc-scroll"
      style={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        padding: '24px 36px 36px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
        }}
      >
        <span
          style={{
            font: '500 11px/1 var(--font-mono)',
            color: 'var(--rc-ink-4)',
            letterSpacing: '0.04em',
            opacity: isSaved ? 1 : 0,
            transition: 'opacity var(--d-short, 80ms) ease',
            flexShrink: 0,
          }}
        >
          Saved
        </span>

        {trashCountdown !== null && (
          <span
            style={{
              font: '500 11px/1 var(--font-mono)',
              color: trashCountdown <= 7 ? 'var(--rc-warn, #d97706)' : 'var(--rc-ink-4)',
              letterSpacing: '0.04em',
              flexShrink: 0,
            }}
          >
            {trashCountdown} {trashCountdown === 1 ? 'day' : 'days'} remaining
          </span>
        )}

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <KickPill track={localTrack} onToggle={toggleTrack} />
          <AutoTitleButton noteId={note.id} body={body} setBody={setBody} />
          <button
            type="button"
            onClick={() => setShowBuckets((v) => !v)}
            aria-label={showBuckets ? 'Back to editor' : 'Grouped read'}
            title={showBuckets ? 'Back to editor' : 'Grouped read'}
            style={hamburgerBtnStyle}
          >
            <Icon name="list" size={14} stroke={1.9} />
          </button>
          <button
            type="button"
            onClick={onTogglePin}
            aria-label={note.pinnedAt ? 'Unpin' : 'Pin'}
            style={iconBtnStyle(!!note.pinnedAt)}
          >
            <Icon name="pin" size={14} stroke={1.9} />
          </button>
          {view === 'active' && (
            <button
              type="button"
              onClick={onArchive}
              aria-label="Archive"
              style={iconBtnStyle(false)}
            >
              <Icon name="archive" size={14} stroke={1.9} />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            aria-label="Delete"
            style={iconBtnStyle(false)}
          >
            <Icon name="trash" size={14} stroke={1.9} />
          </button>
        </div>
      </header>

      {showBuckets ? (
        <BucketView buckets={buckets} body={body} onToggleCheckbox={commitBody} />
      ) : (
        <>
          <GraduationCard body={body} />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Dump stuff in. Jotly figures it out."
            spellCheck
            style={{
              flex: 1,
              minHeight: 240,
              resize: 'none',
              appearance: 'none',
              border: '1px solid var(--rc-border)',
              borderRadius: 12,
              padding: '16px 18px',
              font: '400 16px/1.55 var(--rc-mono, ui-monospace, monospace)',
              color: 'var(--rc-ink)',
              background: 'var(--bg-elev)',
              outline: 'none',
            }}
          />
          <FollowUpStrip
            followUps={followUps}
            expanded={followUpExpanded}
            onToggle={() => setFollowUpExpanded((v) => !v)}
          />
          {voiceTranscript && (
            <div style={voiceFoldoutContainerStyle}>
              <button
                type="button"
                onClick={() => setVoiceFoldoutOpen((v) => !v)}
                style={voiceFoldoutToggleStyle}
              >
                <Icon name="mic" size={12} stroke={2} />
                <span>From voice</span>
                <Icon
                  name="chev-right"
                  size={11}
                  stroke={2}
                  style={{
                    transform: voiceFoldoutOpen ? 'rotate(90deg)' : 'none',
                    transition: 'transform var(--d-short, 80ms) ease',
                  }}
                />
              </button>
              {voiceFoldoutOpen && (
                <div style={voiceTranscriptStyle}>
                  {voiceTranscript}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {note.enrichmentStatus === 'complete' && note.summary && (
        <p
          style={{
            margin: '20px 0 0',
            font: '400 13.5px/1.55 var(--rc-sans)',
            color: 'var(--rc-ink-3)',
            textWrap: 'pretty',
          }}
        >
          {note.summary}
        </p>
      )}
      {note.enrichmentStatus === 'complete' && note.tags.length > 0 && (
        <p
          style={{
            margin: '10px 0 0',
            font: '500 12px/1.5 var(--rc-mono, ui-monospace, monospace)',
            color: 'var(--rc-ink-3)',
            letterSpacing: '0.02em',
          }}
        >
          {note.tags.map((t) => `#${t}`).join('  ·  ')}
        </p>
      )}
      {note.enrichmentStatus === 'failed' && (
        <p
          style={{
            margin: '20px 0 0',
            font: '400 12.5px/1.5 var(--rc-sans)',
            color: 'var(--rc-warn)',
          }}
        >
          Tagging failed.
        </p>
      )}
    </div>
  );
}

// --- FollowUpStrip ---

function FollowUpStrip({
  followUps,
  expanded,
  onToggle,
}: {
  followUps: FollowUp[];
  expanded: boolean;
  onToggle: () => void;
}) {
  if (followUps.length === 0) return null;

  return (
    <div style={followUpStripStyle}>
      {!expanded ? (
        <button type="button" onClick={onToggle} style={followUpChipStyle}>
          <span>{followUps.length} to follow up</span>
          <Icon name="chev-right" size={11} stroke={2.2} />
        </button>
      ) : (
        <>
          <button type="button" onClick={onToggle} style={followUpChipStyle}>
            <span>{followUps.length} to follow up</span>
            <Icon
              name="chev-right"
              size={11}
              stroke={2.2}
              style={{ transform: 'rotate(90deg)' }}
            />
          </button>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {followUps.map((fu) => (
              <FollowUpRow key={fu.key} followUp={fu} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FollowUpRow({ followUp }: { followUp: FollowUp }) {
  const [decision, setDecision] = useState<'keep' | 'drop' | null>(null);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        font: '400 14px/1.45 var(--rc-sans)',
        color: decision === 'drop' ? 'var(--rc-ink-4)' : 'var(--rc-ink)',
        textDecoration: decision === 'drop' ? 'line-through' : 'none',
      }}
    >
      <span style={{ flex: 1 }}>{followUp.text}</span>
      {followUp.person && <PersonBadge name={followUp.person} />}
      {followUp.conf === 'maybe' && decision === null && (
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => setDecision('keep')}
            style={followUpDecisionBtnStyle(false)}
          >
            keep
          </button>
          <button
            type="button"
            onClick={() => setDecision('drop')}
            style={followUpDecisionBtnStyle(true)}
          >
            drop
          </button>
        </div>
      )}
      {followUp.conf === 'maybe' && decision === 'keep' && (
        <span style={{ font: '500 10.5px/1 var(--font-mono)', color: 'var(--rc-accent)', flexShrink: 0 }}>kept</span>
      )}
    </div>
  );
}

function PersonBadge({ name }: { name: string }) {
  return (
    <span style={personBadgeStyle}>
      {name}
    </span>
  );
}

// --- BucketView (grouped read) ---

function BucketView({
  buckets,
  body,
  onToggleCheckbox,
}: {
  buckets: Buckets;
  body: string;
  onToggleCheckbox: (nextBody: string) => void;
}) {
  const lines = body.split('\n');

  const toggleAt = (index: number) => {
    const next = lines
      .map((line, i) => {
        if (i !== index) return line;
        const m = line.match(CHECKBOX_RE);
        if (!m) return line;
        const state = markToState(m[2]);
        return `${m[1]}- [${state === 'done' ? ' ' : 'x'}] ${m[3]}`;
      })
      .join('\n');
    onToggleCheckbox(next);
  };

  const hasTodo = buckets.todo.length > 0;
  const hasQuestions = buckets.questions.length > 0;
  const hasWaiting = buckets.waiting.length > 0;
  const hasKeypoints = buckets.keypoints.length > 0;
  const isEmpty = !hasTodo && !hasQuestions && !hasWaiting && !hasKeypoints;

  if (isEmpty) {
    return (
      <div style={{ ...readViewStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ font: '400 14px/1.4 var(--rc-sans)', color: 'var(--rc-ink-4)' }}>
          Nothing to group yet. Write more and Jotly will sort it.
        </span>
      </div>
    );
  }

  return (
    <div className="rc-scroll" style={readViewStyle}>
      {hasTodo && (
        <div style={{ marginBottom: 18 }}>
          <div style={bucketSectionHeaderStyle}>To-do</div>
          {buckets.todo.map((item, idx) => {
            // Find the corresponding line index for checkbox toggling
            const lineIdx = findCheckboxLineIndex(lines, item.text);
            return (
              <div key={idx} style={checkRowStyle}>
                <button
                  type="button"
                  onClick={() => lineIdx !== -1 && toggleAt(lineIdx)}
                  aria-label={item.done ? 'Mark not done' : 'Mark done'}
                  style={checkBoxStyle(item.done ? 'done' : 'todo')}
                >
                  {item.done && <Icon name="check" size={11} stroke={2.6} />}
                </button>
                <span
                  style={{
                    flex: 1,
                    textDecoration: item.done ? 'line-through' : 'none',
                    color: item.done ? 'var(--rc-ink-3)' : 'var(--rc-ink)',
                  }}
                >
                  {item.text}
                </span>
                {item.person && <PersonBadge name={item.person} />}
              </div>
            );
          })}
        </div>
      )}

      {hasQuestions && (
        <div style={{ marginBottom: 18 }}>
          <div style={bucketSectionHeaderStyle}>Open questions</div>
          {buckets.questions.map((q, idx) => (
            <div key={idx} style={bucketBulletStyle}>
              <span style={{ color: 'var(--rc-accent)', flexShrink: 0, fontWeight: 600 }}>?</span>
              <span>{q}</span>
            </div>
          ))}
        </div>
      )}

      {hasWaiting && (
        <div style={{ marginBottom: 18 }}>
          <div style={bucketSectionHeaderStyle}>Waiting on others</div>
          {buckets.waiting.map((w, idx) => (
            <div key={idx} style={bucketBulletStyle}>
              <Icon name="clock" size={13} stroke={1.8} style={{ flexShrink: 0, color: 'var(--rc-ink-3)' }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {hasKeypoints && (
        <div style={{ marginBottom: 18 }}>
          <div style={bucketSectionHeaderStyle}>Key points</div>
          {buckets.keypoints.map((kp, idx) => (
            <div key={idx} style={bucketBulletStyle}>
              <span style={{ color: 'var(--rc-accent)' }}>{'·'}</span>
              <span>{kp}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function findCheckboxLineIndex(lines: string[], text: string): number {
  return lines.findIndex((line) => {
    const m = line.match(CHECKBOX_RE);
    return m && m[3].trim() === text.trim();
  });
}

// --- KickPill (editor header) ---

function KickPill({ track, onToggle }: { track: string; onToggle: () => void }) {
  const dotColour = track === 'Home' ? '#5f9e6a' : 'var(--accent)';
  return (
    <button
      type="button"
      onClick={onToggle}
      style={kickPillStyle}
      title={`Switch to ${track === 'Work' ? 'Home' : 'Work'}`}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColour, flexShrink: 0 }} />
      {track}
    </button>
  );
}

// --- AutoTitleButton ---

function AutoTitleButton({
  noteId,
  body,
  setBody,
}: {
  noteId: string;
  body: string;
  setBody: (b: string) => void;
}) {
  const autoTitle = useAutoTitleNote();
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle');
  const [previousTitle, setPreviousTitle] = useState<string | null>(null);
  const doneTimer = useRef<number | undefined>(undefined);

  const handleClick = async () => {
    if (state === 'done' && previousTitle !== null) {
      const lines = body.split('\n');
      if (lines[0].startsWith('# ')) {
        lines[0] = `# ${previousTitle}`;
        setBody(lines.join('\n'));
      }
      setPreviousTitle(null);
      setState('idle');
      return;
    }

    setState('busy');
    try {
      const lines = body.split('\n');
      const oldTitle = lines[0]?.startsWith('# ') ? lines[0].replace(/^# /, '') : '';
      setPreviousTitle(oldTitle);

      const result = await autoTitle.mutateAsync(noteId);
      const newLines = body.split('\n');
      if (newLines[0]?.startsWith('# ')) {
        newLines[0] = `# ${result.title}`;
      } else {
        newLines.unshift(`# ${result.title}`);
      }
      setBody(newLines.join('\n'));
      setState('done');

      if (doneTimer.current) window.clearTimeout(doneTimer.current);
      doneTimer.current = window.setTimeout(() => {
        setState('idle');
        setPreviousTitle(null);
      }, 5000);
    } catch {
      setState('idle');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={state === 'busy'}
      style={autoTitleBtnStyle}
      title="Generate title with AI"
    >
      {state === 'idle' && (
        <>
          <Icon name="sparkle" size={13} stroke={2} />
          <span>AI title</span>
        </>
      )}
      {state === 'busy' && (
        <>
          <span style={spinnerStyle} />
          <span>Naming…</span>
        </>
      )}
      {state === 'done' && (
        <>
          <span>Undo</span>
          <span style={aiTagStyle}>AI</span>
        </>
      )}
    </button>
  );
}

// --- Read view ---

const CHECKBOX_RE = /^(\s*)[-*]\s+\[( |x|X|\/|!)\]\s+(.*)$/;

type TaskState = 'todo' | 'doing' | 'done' | 'urgent';

function markToState(mark: string): TaskState {
  if (mark === '/' ) return 'doing';
  if (mark === '!' ) return 'urgent';
  if (mark.toLowerCase() === 'x') return 'done';
  return 'todo';
}

function taskStateCounts(body: string): { todo: number; doing: number; urgent: number; done: number } {
  return {
    todo: (body.match(/\[ \]/g) || []).length,
    doing: (body.match(/\[\/\]/g) || []).length,
    urgent: (body.match(/\[!\]/g) || []).length,
    done: (body.match(/\[x\]/gi) || []).length,
  };
}

// --- GraduationCard ---

function GraduationCard({ body }: { body: string }) {
  const lines = body.split('\n');
  const unchecked = lines
    .map((line, i) => ({ line, i }))
    .filter(({ line }) => {
      const m = line.match(CHECKBOX_RE);
      return m && m[2] !== 'x' && m[2] !== 'X';
    });

  const [graduated, setGraduated] = useState<Record<number, 'pending' | 'done' | 'error'>>({});

  if (unchecked.length === 0) return null;

  const first = unchecked[0];
  const label = first.line.match(CHECKBOX_RE)![3];

  if (graduated[first.i] === 'done') return null;

  const handleRemember = async () => {
    setGraduated((g) => ({ ...g, [first.i]: 'pending' }));
    try {
      await sendMessage(label);
      setGraduated((g) => ({ ...g, [first.i]: 'done' }));
    } catch {
      setGraduated((g) => ({ ...g, [first.i]: 'error' }));
    }
  };

  return (
    <div style={graduationCardStyle}>
      <div style={graduationHeaderStyle}>
        <Icon name="sparkle" size={13} stroke={2} color="var(--accent)" />
        <span>Looks like a thing to remember</span>
      </div>
      <div style={graduationBodyStyle}>
        <span style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>
          {'“'}{label}{'”'}
        </span>
        <button
          type="button"
          onClick={handleRemember}
          disabled={graduated[first.i] === 'pending'}
          style={rememberBtnStyle}
        >
          {graduated[first.i] === 'pending' ? 'Sending…' : graduated[first.i] === 'error' ? 'Retry' : '+ Remember'}
        </button>
      </div>
      <div style={graduationFooterStyle}>
        I can surface this on Now and nudge you — the note keeps the rest.
      </div>
    </div>
  );
}

// --- Undo toast ---

function UndoToast({
  message,
  onUndo,
  onDismiss,
}: {
  message: string;
  onUndo?: () => void;
  onDismiss: () => void;
}) {
  return (
    <div style={toastStyle}>
      <span style={{ flex: 1, font: '400 14px/1.3 var(--font-body)', color: 'var(--ink)' }}>
        {message}
      </span>
      {onUndo && (
        <button type="button" onClick={onUndo} style={toastUndoBtnStyle}>
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--ink-3)', padding: 4 }}
      >
        <Icon name="x" size={14} stroke={2} />
      </button>
    </div>
  );
}

// --- Empty / error / blank states ---

function EmptyState({ view, onNew, pending }: { view: NoteView; onNew: () => void; pending: boolean }) {
  if (view === 'archive') {
    return (
      <div style={emptyStyle}>
        <div style={{ font: '500 15px/1.4 var(--rc-sans)', color: 'var(--rc-ink-2)' }}>
          Nothing archived.
        </div>
      </div>
    );
  }
  if (view === 'trash') {
    return (
      <div style={emptyStyle}>
        <div style={{ font: '500 15px/1.4 var(--rc-sans)', color: 'var(--rc-ink-2)' }}>
          Trash is empty.
        </div>
      </div>
    );
  }
  return (
    <div style={emptyStyle}>
      <div style={{ font: '600 17px/1.3 var(--rc-display)', color: 'var(--rc-ink)', marginBottom: 8 }}>
        Nothing jotted here yet.
      </div>
      <div style={{ font: '400 14px/1.5 var(--rc-sans)', color: 'var(--rc-ink-2)', maxWidth: 280, textAlign: 'center', marginBottom: 16 }}>
        When you fling something in that grows into a thread, it'll graduate here as a note — no filing required.
      </div>
      <button
        type="button"
        onClick={onNew}
        disabled={pending}
        style={{
          appearance: 'none',
          border: '1px solid var(--rc-accent)',
          color: 'var(--rc-accent)',
          background: 'var(--rc-accent-soft)',
          padding: '8px 14px',
          borderRadius: 999,
          cursor: pending ? 'wait' : 'pointer',
          font: '500 13px/1 var(--rc-sans)',
        }}
      >
        {pending ? 'Creating…' : 'New note'}
      </button>
    </div>
  );
}

function EditorBlank({ onNew, pending }: { onNew: () => void; pending: boolean }) {
  return (
    <div style={{ ...emptyStyle, padding: 48 }}>
      <div style={{ font: '400 14px/1.4 var(--rc-sans)', color: 'var(--rc-ink-4)' }}>
        Pick a note from the left, or
      </div>
      <button
        type="button"
        onClick={onNew}
        disabled={pending}
        style={{
          marginTop: 10,
          appearance: 'none',
          border: 0,
          cursor: pending ? 'wait' : 'pointer',
          background: 'transparent',
          color: 'var(--rc-accent)',
          font: '500 14px/1 var(--rc-sans)',
        }}
      >
        {pending ? 'Creating…' : 'start a new one →'}
      </button>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div style={{ font: '400 13px/1.4 var(--rc-sans)', color: 'var(--rc-ink-4)' }}>Loading…</div>
  );
}

function ListError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  const notDeployed = /404/.test(message);
  return (
    <div style={{ font: '400 13px/1.45 var(--rc-sans)', color: 'var(--rc-ink-3)' }}>
      {notDeployed
        ? "Notes API isn't deployed yet. Ask Rob to run sam deploy."
        : `Couldn't load notes: ${message}`}
    </div>
  );
}

// --- Styles ---

const newBtnStyle: React.CSSProperties = {
  width: 30,
  height: 30,
  appearance: 'none',
  border: 0,
  color: '#fff',
  background: 'var(--rc-accent)',
  borderRadius: 999,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

function iconBtnStyle(active: boolean): React.CSSProperties {
  return {
    width: 32,
    height: 32,
    appearance: 'none',
    border: active ? '1px solid var(--rc-accent)' : '1px solid var(--rc-border)',
    color: active ? 'var(--rc-accent)' : 'var(--rc-ink-3)',
    background: active ? 'var(--rc-accent-soft)' : 'transparent',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

const emptyStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  textAlign: 'center',
};

const hamburgerBtnStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  appearance: 'none',
  border: '1px solid var(--rc-border)',
  background: 'transparent',
  borderRadius: 8,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--rc-ink-3)',
};

const readViewStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 240,
  overflowY: 'auto',
  border: '1px solid var(--rc-border)',
  borderRadius: 12,
  padding: '16px 18px',
  font: '400 16px/1.55 var(--rc-sans)',
  color: 'var(--rc-ink)',
  background: 'var(--bg-elev)',
};

const checkRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '6px 0',
};

function checkBoxStyle(state: TaskState): React.CSSProperties {
  const colours: Record<TaskState, { border: string; bg: string }> = {
    todo: { border: 'var(--task-todo-box)', bg: 'transparent' },
    doing: { border: 'var(--task-doing-box)', bg: 'transparent' },
    done: { border: 'var(--task-done-box)', bg: 'var(--task-done-box)' },
    urgent: { border: 'var(--task-urgent-box)', bg: 'transparent' },
  };
  const c = colours[state];
  return {
    width: 18,
    height: 18,
    flexShrink: 0,
    appearance: 'none',
    border: `1.5px solid ${c.border}`,
    background: c.bg,
    color: '#fff',
    borderRadius: 5,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function listPillStyle(state: TaskState): React.CSSProperties {
  const map: Record<TaskState, { bg: string; color: string }> = {
    todo: { bg: 'var(--task-todo-pill)', color: 'var(--task-todo-text)' },
    doing: { bg: 'var(--task-doing-pill)', color: 'var(--task-doing-text)' },
    done: { bg: 'var(--task-done-pill)', color: 'var(--task-done-text)' },
    urgent: { bg: 'var(--task-urgent-pill)', color: 'var(--task-urgent-text)' },
  };
  const s = map[state];
  return {
    font: '700 9px/1 var(--font-mono)',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    background: s.bg,
    color: s.color,
    padding: '2px 6px',
    borderRadius: 999,
  };
}

// View tabs

const viewTabsBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: 2,
  marginBottom: 14,
  background: 'var(--bg-sunken)',
  borderRadius: 999,
  padding: 3,
};

function viewTabStyle(active: boolean): React.CSSProperties {
  return {
    flex: 1,
    appearance: 'none',
    border: 0,
    cursor: 'pointer',
    background: active ? 'var(--bg-elev)' : 'transparent',
    color: active ? 'var(--ink)' : 'var(--ink-3)',
    padding: '6px 8px',
    borderRadius: 999,
    font: '600 11px/1 var(--font-mono)',
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    transition: `background var(--d-short) var(--ease), color var(--d-short) var(--ease)`,
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
  };
}

function tabCountStyle(active: boolean): React.CSSProperties {
  return {
    font: '600 10px/1 var(--font-mono)',
    color: active ? 'var(--accent)' : 'var(--ink-faint)',
    background: active ? 'var(--accent-soft)' : 'transparent',
    borderRadius: 999,
    padding: '2px 5px',
    minWidth: 18,
    textAlign: 'center',
  };
}

// Hover actions

const hoverActionsStyle: React.CSSProperties = {
  position: 'absolute',
  right: 8,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  gap: 2,
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: 2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};

function hoverBtnStyle(active?: boolean): React.CSSProperties {
  return {
    width: 24,
    height: 24,
    appearance: 'none',
    border: 0,
    cursor: 'pointer',
    background: 'transparent',
    color: active ? 'var(--accent)' : 'var(--ink-3)',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// KickChip (list row)

const kickChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  font: '500 11px/1 var(--font-mono)',
  color: 'var(--ink-3)',
  letterSpacing: '0.02em',
  marginTop: 2,
};

// KickPill (editor header)

const kickPillStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid var(--border)',
  background: 'transparent',
  borderRadius: 999,
  padding: '4px 10px 4px 8px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  cursor: 'pointer',
  font: '500 12px/1 var(--font-mono)',
  color: 'var(--ink-2)',
  letterSpacing: '0.02em',
};

// AutoTitle button

const autoTitleBtnStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid var(--border)',
  background: 'transparent',
  borderRadius: 999,
  padding: '5px 10px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  cursor: 'pointer',
  font: '500 12px/1 var(--font-mono)',
  color: 'var(--ink-2)',
  letterSpacing: '0.02em',
};

const spinnerStyle: React.CSSProperties = {
  width: 12,
  height: 12,
  border: '2px solid var(--border-strong)',
  borderTopColor: 'var(--accent)',
  borderRadius: '50%',
  animation: 'spin 0.6s linear infinite',
};

const aiTagStyle: React.CSSProperties = {
  font: '700 9px/1 var(--font-mono)',
  color: 'var(--accent)',
  background: 'var(--accent-soft)',
  borderRadius: 4,
  padding: '2px 4px',
  letterSpacing: '0.06em',
};

// GraduationCard

const graduationCardStyle: React.CSSProperties = {
  margin: '20px 0 0',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '14px 16px',
  background: 'var(--bg-elev)',
};

const graduationHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  font: '600 11px/1 var(--font-mono)',
  color: 'var(--ink-3)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: 10,
};

const graduationBodyStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  font: '400 15px/1.45 var(--font-body)',
  marginBottom: 10,
};

const rememberBtnStyle: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  background: 'var(--accent)',
  color: '#fff',
  borderRadius: 999,
  padding: '6px 12px',
  cursor: 'pointer',
  font: '600 12px/1 var(--font-body)',
  flexShrink: 0,
};

const graduationFooterStyle: React.CSSProperties = {
  font: '400 12px/1.5 var(--font-body)',
  color: 'var(--ink-faint)',
};

// FollowUpStrip

const followUpStripStyle: React.CSSProperties = {
  border: '1px solid var(--rc-border)',
  borderRadius: 10,
  padding: '12px 14px',
  marginTop: 10,
};

const followUpChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--rc-accent-soft)',
  border: '1px solid var(--rc-border)',
  borderRadius: 999,
  padding: '5px 12px',
  cursor: 'pointer',
  font: '500 12px/1 var(--font-mono)',
  color: 'var(--rc-accent)',
  appearance: 'none',
};

function followUpDecisionBtnStyle(faint: boolean): React.CSSProperties {
  return {
    appearance: 'none',
    border: faint ? '1px solid var(--rc-border)' : '1px solid var(--rc-accent)',
    background: faint ? 'transparent' : 'transparent',
    color: faint ? 'var(--rc-ink-4)' : 'var(--rc-accent)',
    borderRadius: 999,
    padding: '3px 8px',
    cursor: 'pointer',
    font: '500 10.5px/1 var(--font-mono)',
    letterSpacing: '0.02em',
  };
}

const personBadgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  background: 'var(--rc-accent-soft)',
  color: 'var(--accent-strong)',
  font: '500 10.5px/1 var(--font-mono)',
  padding: '2px 6px',
  borderRadius: 999,
  flexShrink: 0,
};

// BucketView

const bucketSectionHeaderStyle: React.CSSProperties = {
  font: '700 11px/1 var(--font-mono)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--rc-ink-3)',
  marginBottom: 8,
};

const bucketBulletStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  padding: '3px 0',
  color: 'var(--rc-ink)',
  font: '400 15px/1.5 var(--rc-sans)',
};

// Voice foldout

const voiceFoldoutContainerStyle: React.CSSProperties = {
  marginTop: 10,
  border: '1px solid var(--rc-border)',
  borderRadius: 10,
  overflow: 'hidden',
};

const voiceFoldoutToggleStyle: React.CSSProperties = {
  width: '100%',
  appearance: 'none',
  border: 0,
  background: 'transparent',
  padding: '10px 14px',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  font: '500 12px/1 var(--font-mono)',
  color: 'var(--rc-ink-3)',
  letterSpacing: '0.02em',
};

const voiceTranscriptStyle: React.CSSProperties = {
  padding: '0 14px 12px',
  font: '400 13.5px/1.55 var(--rc-sans)',
  color: 'var(--rc-ink-3)',
  whiteSpace: 'pre-wrap',
};

// Toast

const toastStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 20,
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  background: 'var(--bg-elev)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '10px 14px',
  boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
  maxWidth: 420,
  minWidth: 260,
  zIndex: 100,
};

const toastUndoBtnStyle: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  background: 'transparent',
  color: 'var(--accent)',
  cursor: 'pointer',
  font: '600 13px/1 var(--font-body)',
  flexShrink: 0,
};
